// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Box } from '@mui/material';
import { ProviderConfiguration } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Configuration/Provider';
import FormDialog from '../../../components/Dialog/FormDialog';
import useArchiveAndCreateProviderConfiguration from '../../../hooks/mutations/registry/configuration/useArchiveAndCreateProviderConfiguration';
import useMutate from '../../../hooks/mutations/useMutate';
import useProviderService from '../../../hooks/queries/registry/onboarding/useProviderService';
import {
  PartyCredentialRequirementElement,
  toPartyCredentialRequirements,
  fromPartyCredentialRequirements,
} from '../../../modules/Credential/Common';
import RequiredCredentialsInput from '../../../modules/Credential/RequiredCredentialsInput';
import TemplateContract from '../../../utils/TemplateContract';
import { SUC_MOD_PROVIDER_CONF, ERR_MOD_PROVIDER_CONF } from '../../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  providerConfiguration: TemplateContract<ProviderConfiguration>;
};

export const ModifyProviderConfigurationDialog = ({
  open,
  onClose,
  providerConfiguration,
}: DialogProps) => {
  const [holderCredentialRequirements, setHolderCredentialRequirements] = useState<
    PartyCredentialRequirementElement[]
  >([]);
  const [registrarCredentialRequirements, setRegistrarCredentialRequirements] = useState<
    PartyCredentialRequirementElement[]
  >([]);

  useEffect(() => {
    if (providerConfiguration?.payload?.holderRequirements) {
      setHolderCredentialRequirements(
        fromPartyCredentialRequirements(providerConfiguration.payload.holderRequirements),
      );
    }
    if (providerConfiguration?.payload?.registrarRequirements) {
      setRegistrarCredentialRequirements(
        fromPartyCredentialRequirements(providerConfiguration.payload.registrarRequirements),
      );
    }
  }, [providerConfiguration]);

  const providerService = useProviderService();
  const archiveAndCreateProviderConfiguration = useArchiveAndCreateProviderConfiguration();
  const mutate = useMutate();
  const { showBoundary } = useErrorBoundary<Error>();

  const resetState = useCallback(() => {
    setHolderCredentialRequirements([]);
    setRegistrarCredentialRequirements([]);
  }, [setHolderCredentialRequirements, setRegistrarCredentialRequirements]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!providerService.data) {
      showBoundary(
        new Error(ERR_MOD_PROVIDER_CONF, { cause: 'Provider service is not available.' }),
      );
      return;
    }
    const payload = {
      providerServiceCid: providerService.data.contractId,
      providerConfigurationCid: providerConfiguration.contractId,
      holderRequirements: toPartyCredentialRequirements(holderCredentialRequirements),
      registrarRequirements: toPartyCredentialRequirements(registrarCredentialRequirements),
    };
    await mutate(
      archiveAndCreateProviderConfiguration,
      payload,
      SUC_MOD_PROVIDER_CONF,
      ERR_MOD_PROVIDER_CONF,
    );
    handleClose();
  }, [
    holderCredentialRequirements,
    registrarCredentialRequirements,
    archiveAndCreateProviderConfiguration,
    providerConfiguration.contractId,
    providerService.data,
    handleClose,
    mutate,
    showBoundary,
  ]);

  if (providerService.isLoading || archiveAndCreateProviderConfiguration.isLoading) return null;

  const disableSubmit = false;
  return (
    <FormDialog
      open={open}
      title="Modify Provider Configuration"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Modify"
      isSubmitDisabled={disableSubmit}
    >
      <Box mb={3}>
        <RequiredCredentialsInput
          list={holderCredentialRequirements}
          setList={setHolderCredentialRequirements}
          title="Required Credentials for Holder"
        />
      </Box>
      <Box mb={3}>
        <RequiredCredentialsInput
          list={registrarCredentialRequirements}
          setList={setRegistrarCredentialRequirements}
          title="Required Credentials for Registrar"
        />
      </Box>
    </FormDialog>
  );
};
